// Podcast Ad's media tiles (live's `media-tiles`, captures 115 to 118 and 127 to 129): the
// product and its packshot, then the host and the guest.
import { CloseButton, Image, Loader, Menu, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Archive, ImageOff, LibraryBig, SmilePlus } from "lucide-react";
import { useState } from "react";

import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { ActorSource, PodcastProductPick } from "../../../api/generated/model";
import { AddActorsDialog } from "../../../platform/actors/AddActorsDialog";
import type { PickedActor } from "../../../platform/actors/picked";
import { failedWords } from "../../../platform/jobs/failed-words";
import { ProductPicker } from "../../../platform/media/ProductPicker";
import { useRefPicture } from "../../../platform/media/ref-picture";
import { selectUpTo } from "../../../ui/format";
import { PICK_PRODUCT } from "./podcast-script";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_TOOLTIP =
  "The product and the exact photo of it the ad is built on. Pick a product, then the packshot " +
  "image to show on the table and the end card.";
export type CastRole = "host" | "guest";
export const ROLES: Readonly<Record<CastRole, { label: string; tooltip: string }>> = {
  host: {
    label: "Host",
    tooltip:
      "The podcast host who asks the questions. Pick an AI Actor or upload a photo — leave empty " +
      "and the director casts one.",
  },
  guest: {
    label: "Guest",
    tooltip:
      "The guest who explains and reveals your product. Pick an AI Actor or upload a photo — " +
      "leave empty and the director casts one.",
  },
};

/** The product's name and its packshot's picture, from the brand's products. */
export function useProductPick(teamId: string, brandId: string, pick: PodcastProductPick | null) {
  const products = useListProducts(teamId, brandId).data ?? [];
  const product = pick === null ? undefined : products.find((each) => each.id === pick.product_id);
  const image = product?.images.find((each) => each.asset_id === pick?.image_id);
  return { name: product?.name ?? "", url: image?.url ?? null };
}

export interface ProductTileProps {
  teamId: string;
  brandId: string;
  pick: PodcastProductPick | null;
  disabled: boolean;
  /** Live's "Pick a product and its packshot", once Generate Script asked for one. */
  missing: boolean;
  onChange: (pick: PodcastProductPick | null) => void;
}

/**
 * Live's product tile (`packshot-tile-field`): empty, the archive box and "Product" with live's
 * tooltip; it opens "Products", a product then its packshot (the main picture chosen first),
 * and Confirm. Filled, the packshot (a click picks again) with an x.
 */
export function ProductTile(props: ProductTileProps) {
  const { teamId, brandId, pick, disabled, missing, onChange } = props;
  const [picking, setPicking] = useState(false);
  const shown = useProductPick(teamId, brandId, pick);
  return (
    <div className={classes.tileField}>
      <ProductPicker
        teamId={teamId}
        brandId={brandId}
        chosen={
          pick === null
            ? null
            : { productId: pick.product_id, assetId: pick.image_id, url: shown.url, name: shown.name }
        }
        subtitle={selectUpTo(1, "product")}
        opened={picking}
        onClose={() => setPicking(false)}
        onConfirm={(choice) => onChange({ product_id: choice.productId, image_id: choice.assetId })}
        anchor={(wrap) =>
          pick === null ? (
            <Tooltip label={PRODUCT_TOOLTIP} multiline maw={280} withArrow disabled={picking}>
              {wrap(
                <UnstyledButton
                  className={classes.tile}
                  aria-label="Product"
                  disabled={disabled}
                  onClick={() => setPicking(true)}
                >
                  <Archive size={20} strokeWidth={1.5} aria-hidden />
                  <span>Product</span>
                </UnstyledButton>,
              )}
            </Tooltip>
          ) : (
            <span className={classes.tileFilled}>
              {wrap(
                <UnstyledButton
                  className={classes.tilePicture}
                  aria-label={`Product: ${shown.name || "the packshot"}`}
                  disabled={disabled}
                  onClick={() => setPicking(true)}
                >
                  {shown.url === null ? null : <Image src={shown.url} alt="" className={classes.tilePicture} />}
                </UnstyledButton>,
              )}
              {disabled ? null : (
                <CloseButton
                  size="xs"
                  className={classes.tileRemove}
                  aria-label="Remove the product"
                  onClick={() => onChange(null)}
                />
              )}
            </span>
          )
        }
      />
      {missing && pick === null ? <Text className={classes.fieldError}>{PICK_PRODUCT}</Text> : null}
    </div>
  );
}

/** An actor "Add AI Actor" is making for a role (live's `hostGeneration` / `guestGeneration`). */
export interface RoleMaking {
  jobId: string;
  failed: boolean;
  role: CastRole;
}

/** The portraits the form saw picked, by their source: a global actor's cannot be read again. */
export type PickedPictures = Readonly<Record<string, { url: string | null; name: string }>>;

export function sourceKey(source: ActorSource): string {
  if (source.kind === "job_output") return `job_output:${source.job_id}:${source.output_index}`;
  return source.kind === "asset" ? `asset:${source.asset_id}` : `${source.kind}:${source.actor_id}`;
}

export interface CastTileProps {
  teamId: string;
  brandId: string;
  /** The person's label ("Host", "Interviewer") and live's tooltip on the empty tile. */
  label: string;
  tooltip: string;
  source: ActorSource | null;
  /** An AI Actor made for the tile: loading, or failed. */
  making: { failed: boolean } | null;
  pictures: PickedPictures;
  disabled: boolean;
  /** Live's "Add AI Actor"; none while no model makes actors. */
  onMakeActor: (() => void) | null;
  onPick: (actor: PickedActor) => void;
  onRemove: () => void;
}

/**
 * Live's one-person tile (`actor-tile-field`: Podcast Ad's host or guest, Street Interview Ad's
 * interviewer): empty, the smiley and its label with live's
 * tooltip, whose menu is "From Library" (Add Actors, its library images live's uploaded photo)
 * and "Add AI Actor" (the actor composer in the form's place). While an actor is made for it, a
 * loader; failed, live's words and an x. Filled, the portrait with an x.
 */
export function CastTile(props: CastTileProps) {
  const { teamId, brandId, label, tooltip, source, making, pictures, disabled, onMakeActor, onPick, onRemove } =
    props;
  const [choosing, setChoosing] = useState(false);
  const read = useRefPicture(teamId, brandId, source);
  if (making !== null) {
    const words = failedWords("image.actor", undefined);
    return (
      <fieldset className={classes.tileFilled} aria-label={`${label}: new AI Actor`}>
        {making.failed ? (
          <span className={`${classes.tile} ${classes.tileFailed}`} role="alert">
            <ImageOff size={20} aria-hidden />
            <span>{words.title}</span>
          </span>
        ) : (
          <span className={classes.tile}>
            <Loader size="sm" aria-label={`Making the ${label.toLowerCase()}`} />
          </span>
        )}
        {making.failed && !disabled ? (
          <CloseButton
            size="xs"
            className={classes.tileRemove}
            aria-label={`Remove the failed ${label.toLowerCase()}`}
            onClick={onRemove}
          />
        ) : null}
      </fieldset>
    );
  }
  if (source !== null) {
    const picked = pictures[sourceKey(source)];
    const picture = picked?.url ?? read.still;
    const name = picked?.name ?? read.name ?? "AI Actor";
    return (
      <fieldset className={classes.tileFilled} aria-label={`${label}: ${name}`}>
        {picture === null ? (
          <span className={classes.tile}>
            <SmilePlus size={20} strokeWidth={1.5} aria-hidden />
            <span>{label}</span>
          </span>
        ) : (
          <Image src={picture} alt="" className={classes.tilePicture} />
        )}
        {disabled ? null : (
          <CloseButton
            size="xs"
            className={classes.tileRemove}
            aria-label={`Remove the ${label.toLowerCase()}`}
            onClick={onRemove}
          />
        )}
      </fieldset>
    );
  }
  return (
    <>
      <Menu position="bottom-start" disabled={disabled}>
        {/* Mantine's way: the tooltip around the menu's target. */}
        <Tooltip label={tooltip} multiline maw={280} withArrow position="right">
          <Menu.Target>
            <UnstyledButton className={classes.tile} aria-label={label} disabled={disabled}>
              <SmilePlus size={20} strokeWidth={1.5} aria-hidden />
              <span>{label}</span>
            </UnstyledButton>
          </Menu.Target>
        </Tooltip>
        <Menu.Dropdown>
          <Menu.Item leftSection={<LibraryBig size={16} strokeWidth={1.5} />} onClick={() => setChoosing(true)}>
            From Library
          </Menu.Item>
          <Menu.Item
            leftSection={<SmilePlus size={16} strokeWidth={1.5} />}
            disabled={onMakeActor === null}
            onClick={onMakeActor ?? undefined}
          >
            Add AI Actor
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {choosing ? (
        <AddActorsDialog
          teamId={teamId}
          brandId={brandId}
          chosen={[]}
          max={1}
          onClose={() => setChoosing(false)}
          onConfirm={(actors) => {
            const actor = actors[0];
            if (actor !== undefined) onPick(actor);
            setChoosing(false);
          }}
        />
      ) : null}
    </>
  );
}
